<template>
  <div
    class="ai-login-page"
    :class="{
      'ai-login-page--landscape': layoutMode === 'landscape',
      'ai-login-page--portrait': layoutMode === 'portrait',
      'ai-login-page--leaving': isLeaving,
    }"
    :style="backgroundStyle"
  >
    <video
      ref="introVideoRef"
      class="login-background-video"
      :class="{
        'login-background-video--visible': visibleBackgroundLayer === 'intro',
      }"
      :src="loginBgIntroSrc"
      :poster="loginBgPosterSrc"
      autoplay
      muted
      playsinline
      preload="auto"
      aria-hidden="true"
      @ended="handleBackgroundIntroEnded"
    ></video>
    <video
      ref="loopPrimaryVideoRef"
      class="login-background-video"
      :class="{
        'login-background-video--visible':
          visibleBackgroundLayer === 'loop-primary',
      }"
      :src="loginBgLoopSrc"
      muted
      playsinline
      preload="auto"
      aria-hidden="true"
      @timeupdate="handleLoopTimeUpdate('loop-primary')"
      @ended="handleLoopEnded('loop-primary')"
    ></video>
    <video
      ref="loopSecondaryVideoRef"
      class="login-background-video"
      :class="{
        'login-background-video--visible':
          visibleBackgroundLayer === 'loop-secondary',
      }"
      :src="loginBgLoopSrc"
      muted
      playsinline
      preload="auto"
      aria-hidden="true"
      @timeupdate="handleLoopTimeUpdate('loop-secondary')"
      @ended="handleLoopEnded('loop-secondary')"
    ></video>
    <AiContinuousGlobe :active="visibleBackgroundLayer !== 'intro'" />
    <AiLoginBackdrop />
    <div class="ambient-light" aria-hidden="true">
      <span class="ambient-light__ray ambient-light__ray--one"></span>
      <span class="ambient-light__ray ambient-light__ray--two"></span>
      <span class="ambient-light__ray ambient-light__ray--three"></span>
      <span class="ambient-light__ray ambient-light__ray--four"></span>
      <span class="ambient-light__particle ambient-light__particle--one"></span>
      <span class="ambient-light__particle ambient-light__particle--two"></span>
      <span
        class="ambient-light__particle ambient-light__particle--three"
      ></span>
      <span
        class="ambient-light__particle ambient-light__particle--four"
      ></span>
      <span
        class="ambient-light__particle ambient-light__particle--five"
      ></span>
      <span class="ambient-light__particle ambient-light__particle--six"></span>
      <span
        class="ambient-light__particle ambient-light__particle--seven"
      ></span>
      <span
        class="ambient-light__particle ambient-light__particle--eight"
      ></span>
      <span
        class="ambient-light__particle ambient-light__particle--nine"
      ></span>
      <span class="ambient-light__particle ambient-light__particle--ten"></span>
    </div>
    <div class="circuit-flow" aria-hidden="true">
      <span class="circuit-flow__trace circuit-flow__trace--one"></span>
      <span class="circuit-flow__trace circuit-flow__trace--two"></span>
      <span class="circuit-flow__trace circuit-flow__trace--three"></span>
      <span class="circuit-flow__trace circuit-flow__trace--four"></span>
      <span class="circuit-flow__trace circuit-flow__trace--five"></span>
      <span class="circuit-flow__trace circuit-flow__trace--six"></span>
      <span class="circuit-flow__node circuit-flow__node--one"></span>
      <span class="circuit-flow__node circuit-flow__node--two"></span>
      <span class="circuit-flow__node circuit-flow__node--three"></span>
    </div>

    <main class="login-stage">
      <div ref="viewportRef" class="login-stage__viewport">
        <div
          class="login-stage__shell"
          :class="{
            'login-stage__shell--portrait': layoutMode === 'portrait',
            'login-stage__shell--landscape': layoutMode === 'landscape',
          }"
          :style="{
            width:
              layoutMode === 'scaled'
                ? `${scaledBounds.width}px`
                : `${naturalShellWidth}px`,
            height: cardScale < 1 ? `${scaledBounds.height}px` : 'auto',
          }"
        >
          <div
            class="login-stage__scale"
            :style="{
              transform:
                cardScale < 1
                  ? `translateY(${visualOffsetY}px) scale(${cardScale})`
                  : `translateY(${visualOffsetY}px)`,
            }"
          >
            <div ref="contentRef" class="login-stage__content">
              <AiHudCard>
                <template #meta>
                  <div class="hero-meta">
                    <div class="meta-copy">
                      <span>VOICE AI</span>
                      <small>智能 · 语言 · 未来</small>
                    </div>
                    <div class="meta-copy meta-copy--right">
                      <span>INTELLIGENCE<br />EDUCATION</span>
                      <div class="meta-dots">
                        <i></i>
                        <i></i>
                        <i></i>
                        <i></i>
                      </div>
                    </div>
                  </div>
                </template>

                <section class="hero-visual">
                  <AiHologramBrain />
                </section>

                <section class="hero-title">
                  <h1>AI 智慧教育<br />知识库问答系统</h1>
                  <p>实践教学平台</p>
                </section>

                <section
                  class="status-banner"
                  :class="`status-banner--${verificationState}`"
                >
                  <span class="status-badge"></span>
                  <div>
                    <strong>{{ verificationTitle }}</strong>
                    <span>{{ verificationDetail }}</span>
                  </div>
                </section>

                <section class="progress-grid">
                  <AiModuleList :items="leftModules" align="left" />

                  <div class="progress-core">
                    <AiProgressRing
                      :progress="progress"
                      :caption="progressCaption"
                      :state="verificationState"
                    />
                  </div>

                  <AiModuleList :items="rightModules" align="right" />
                </section>

                <section class="auth-block">
                  <div class="auth-field-wrap">
                    <div class="auth-icon">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          d="M12 1.75a5.25 5.25 0 0 0-5.25 5.25v2.75A3.25 3.25 0 0 0 3.5 13v6.5A3.25 3.25 0 0 0 6.75 22.75h10.5A3.25 3.25 0 0 0 20.5 19.5V13a3.25 3.25 0 0 0-3.25-3.25V7A5.25 5.25 0 0 0 12 1.75Zm-2.25 8V7a2.25 2.25 0 0 1 4.5 0v2.75h-4.5Z"
                        />
                      </svg>
                    </div>
                    <input
                      :value="authorizationCode"
                      :type="showPassword ? 'text' : 'password'"
                      class="auth-input"
                      placeholder=""
                      :disabled="isSubmitting"
                      @input="handleInput"
                      @keydown.enter.prevent="$emit('submit')"
                    />
                    <button
                      type="button"
                      class="auth-visibility"
                      :aria-label="showPassword ? '隐藏授权码' : '查看授权码'"
                      @click="togglePasswordVisibility"
                    >
                      <svg
                        v-if="!showPassword"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 5.25c-4.55 0-8.28 2.63-10.1 6.34a.92.92 0 0 0 0 .82c1.82 3.71 5.55 6.34 10.1 6.34s8.28-2.63 10.1-6.34a.92.92 0 0 0 0-.82C20.28 7.88 16.55 5.25 12 5.25Zm0 10.25a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Zm0-2a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
                        />
                      </svg>
                      <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          d="M3.28 2.22a.75.75 0 0 0-1.06 1.06l3.1 3.1A11.66 11.66 0 0 0 1.9 11.59a.92.92 0 0 0 0 .82c1.82 3.71 5.55 6.34 10.1 6.34 1.8 0 3.47-.41 4.93-1.14l3.79 3.79a.75.75 0 1 0 1.06-1.06L3.28 2.22Zm8.72 13.28a3.5 3.5 0 0 1-3.5-3.5c0-.45.09-.89.25-1.28l4.53 4.53c-.39.16-.83.25-1.28.25Zm0-10.25c4.55 0 8.28 2.63 10.1 6.34a.92.92 0 0 1 0 .82 12.01 12.01 0 0 1-2.42 3.24l-3.04-3.04A4.75 4.75 0 0 0 10.1 6.07l-1.8-1.8c1.15-.65 2.4-1.02 3.7-1.02Z"
                        />
                      </svg>
                    </button>
                    <div
                      class="auth-state"
                      :class="`auth-state--${verificationState}`"
                    >
                      <span class="auth-state__dot"></span>
                      <span>{{ verificationStateLabel }}</span>
                    </div>
                  </div>
                </section>

                <section class="action-block">
                  <AiGradientButton
                    :label="buttonLabel"
                    :hint="buttonHint"
                    :loading="isSubmitting"
                    :state="verificationState"
                    :ripple-key="buttonRippleId"
                    @click="$emit('submit')"
                  />
                </section>

                <footer class="hero-footer">
                  <span class="hero-footer__wave"></span>
                  <span>智能语言交互 · 让学习更高效</span>
                  <span class="hero-footer__line"></span>
                </footer>
              </AiHudCard>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  type PropType,
} from 'vue';
import AiContinuousGlobe from './AiContinuousGlobe.vue';
import AiGradientButton from './AiGradientButton.vue';
import AiHologramBrain from './AiHologramBrain.vue';
import AiHudCard from './AiHudCard.vue';
import AiLoginBackdrop from './AiLoginBackdrop.vue';
import AiModuleList from './AiModuleList.vue';
import AiProgressRing from './AiProgressRing.vue';

type VerificationState = 'idle' | 'verifying' | 'verified' | 'error';
type ModuleStatus = 'done' | 'active' | 'pending';
type BackgroundLayer = 'intro' | 'loop-primary' | 'loop-secondary';

interface ModuleItem {
  title: string;
  desc: string;
  status: ModuleStatus;
}

export default defineComponent({
  name: 'AiLoginPage',
  components: {
    AiContinuousGlobe,
    AiGradientButton,
    AiHologramBrain,
    AiHudCard,
    AiLoginBackdrop,
    AiModuleList,
    AiProgressRing,
  },
  props: {
    authorizationCode: {
      type: String,
      required: true,
    },
    progress: {
      type: Number,
      required: true,
    },
    verificationState: {
      type: String as PropType<VerificationState>,
      required: true,
    },
    verificationTitle: {
      type: String,
      required: true,
    },
    verificationDetail: {
      type: String,
      required: true,
    },
    progressCaption: {
      type: String,
      required: true,
    },
    verificationStateLabel: {
      type: String,
      required: true,
    },
    buttonLabel: {
      type: String,
      required: true,
    },
    buttonHint: {
      type: String,
      required: true,
    },
    leftModules: {
      type: Array as PropType<ModuleItem[]>,
      required: true,
    },
    rightModules: {
      type: Array as PropType<ModuleItem[]>,
      required: true,
    },
    isSubmitting: {
      type: Boolean,
      required: true,
    },
    isLeaving: {
      type: Boolean,
      default: false,
    },
    buttonRippleId: {
      type: Number,
      required: true,
    },
  },
  emits: ['submit', 'update:authorization-code'],
  setup(_, { emit }) {
    const viewportRef = ref<HTMLElement | null>(null);
    const contentRef = ref<HTMLElement | null>(null);
    const showPassword = ref(false);
    const cardScale = ref(1);
    const layoutMode = ref<'scaled' | 'portrait' | 'landscape'>('scaled');
    const naturalShellWidth = ref(0);
    const scaledBounds = ref({ width: 0, height: 0 });
    const visualOffsetY = ref(-42);
    const introVideoRef = ref<HTMLVideoElement | null>(null);
    const loopPrimaryVideoRef = ref<HTMLVideoElement | null>(null);
    const loopSecondaryVideoRef = ref<HTMLVideoElement | null>(null);
    const visibleBackgroundLayer = ref<BackgroundLayer>('intro');
    const loginBgVersion = 'seamless-long-20260609-1';
    const loginBgIntroSrc = `${process.env.BASE_URL}login-bg-intro.mp4?v=${loginBgVersion}`;
    const loginBgLoopSrc = `${process.env.BASE_URL}login-bg-loop.mp4?v=${loginBgVersion}`;
    const loginBgPosterSrc = `${process.env.BASE_URL}login-bg-poster.jpg?v=${loginBgVersion}`;
    const backgroundStyle = {
      '--login-bg-image': `url("${loginBgPosterSrc}")`,
    };
    let resizeObserver: ResizeObserver | null = null;
    let animationFrameId = 0;
    let hasSwitchedBackgroundLoop = false;
    let isLoopCrossfading = false;
    let backgroundFadeTimer: number | undefined;

    const BACKGROUND_CROSSFADE_SECONDS = 0.18;
    const BACKGROUND_CROSSFADE_MS = 180;

    const fitCardToViewport = (): void => {
      const viewport = viewportRef.value;
      const content = contentRef.value;

      if (!viewport || !content) {
        return;
      }

      const stage = viewport.parentElement;
      const stageStyles = stage ? window.getComputedStyle(stage) : null;
      const horizontalPadding =
        (stageStyles ? parseFloat(stageStyles.paddingLeft) : 0) +
        (stageStyles ? parseFloat(stageStyles.paddingRight) : 0);
      const verticalPadding =
        (stageStyles ? parseFloat(stageStyles.paddingTop) : 0) +
        (stageStyles ? parseFloat(stageStyles.paddingBottom) : 0);
      const visualViewport = window.visualViewport;
      const availableWidth =
        (visualViewport?.width || window.innerWidth) - horizontalPadding;
      const availableHeight =
        (visualViewport?.height || window.innerHeight) - verticalPadding;
      const viewportWidth = Math.max(
        Math.min(viewport.clientWidth, availableWidth),
        320
      );
      const viewportHeight = Math.max(availableHeight, 320);
      layoutMode.value = 'scaled';

      const contentWidth = content.offsetWidth;
      const contentHeight = content.offsetHeight;

      if (!contentWidth || !contentHeight) {
        return;
      }

      const nextScale = Math.min(
        1,
        viewportWidth / contentWidth,
        viewportHeight / contentHeight
      );
      const nextVisualOffsetY = Math.round(-42 * nextScale);

      cardScale.value = nextScale;
      visualOffsetY.value = nextVisualOffsetY;
      scaledBounds.value = {
        width: Math.ceil(contentWidth * nextScale),
        height: Math.ceil(
          contentHeight * nextScale + Math.abs(nextVisualOffsetY)
        ),
      };
    };

    const scheduleFitCard = (): void => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = 0;
        fitCardToViewport();
      });
    };

    const handleInput = (event: Event): void => {
      const target = event.target as HTMLInputElement;
      emit('update:authorization-code', target.value);
    };

    const togglePasswordVisibility = (): void => {
      showPassword.value = !showPassword.value;
    };

    const playBackgroundVideo = (video: HTMLVideoElement): void => {
      void video.play().catch(() => undefined);
    };

    const loopVideoForLayer = (
      layer: Exclude<BackgroundLayer, 'intro'>
    ): HTMLVideoElement | null =>
      layer === 'loop-primary'
        ? loopPrimaryVideoRef.value
        : loopSecondaryVideoRef.value;

    const oppositeLoopLayer = (
      layer: Exclude<BackgroundLayer, 'intro'>
    ): Exclude<BackgroundLayer, 'intro'> =>
      layer === 'loop-primary' ? 'loop-secondary' : 'loop-primary';

    const resetHiddenLoopVideo = (video: HTMLVideoElement | null): void => {
      if (!video) return;
      video.pause();
      video.currentTime = 0;
    };

    const finishBackgroundCrossfade = (
      previousLayer: Exclude<BackgroundLayer, 'intro'>
    ): void => {
      resetHiddenLoopVideo(loopVideoForLayer(previousLayer));
      isLoopCrossfading = false;
      backgroundFadeTimer = undefined;
    };

    const startLoopCrossfade = (
      currentLayer: Exclude<BackgroundLayer, 'intro'>
    ): void => {
      if (isLoopCrossfading || visibleBackgroundLayer.value !== currentLayer) {
        return;
      }

      const nextLayer = oppositeLoopLayer(currentLayer);
      const nextVideo = loopVideoForLayer(nextLayer);

      if (!nextVideo) {
        return;
      }

      isLoopCrossfading = true;
      nextVideo.currentTime = 0;
      playBackgroundVideo(nextVideo);
      visibleBackgroundLayer.value = nextLayer;

      if (backgroundFadeTimer) {
        window.clearTimeout(backgroundFadeTimer);
      }

      backgroundFadeTimer = window.setTimeout(() => {
        finishBackgroundCrossfade(currentLayer);
      }, BACKGROUND_CROSSFADE_MS);
    };

    const handleLoopTimeUpdate = (
      layer: Exclude<BackgroundLayer, 'intro'>
    ): void => {
      const video = loopVideoForLayer(layer);

      if (
        !video ||
        visibleBackgroundLayer.value !== layer ||
        isLoopCrossfading ||
        !Number.isFinite(video.duration)
      ) {
        return;
      }

      if (video.duration - video.currentTime <= BACKGROUND_CROSSFADE_SECONDS) {
        startLoopCrossfade(layer);
      }
    };

    const handleLoopEnded = (
      layer: Exclude<BackgroundLayer, 'intro'>
    ): void => {
      if (visibleBackgroundLayer.value === layer && !isLoopCrossfading) {
        startLoopCrossfade(layer);
      }
    };

    const handleBackgroundIntroEnded = (): void => {
      const loopVideo = loopPrimaryVideoRef.value;

      if (!loopVideo || hasSwitchedBackgroundLoop) {
        return;
      }

      hasSwitchedBackgroundLoop = true;
      loopVideo.currentTime = 0;
      playBackgroundVideo(loopVideo);
      visibleBackgroundLayer.value = 'loop-primary';

      window.setTimeout(() => {
        introVideoRef.value?.pause();
      }, BACKGROUND_CROSSFADE_MS);
    };

    onMounted(async () => {
      await nextTick();
      scheduleFitCard();

      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          scheduleFitCard();
        });

        if (viewportRef.value) {
          resizeObserver.observe(viewportRef.value);
        }

        if (contentRef.value) {
          resizeObserver.observe(contentRef.value);
        }
      }

      window.addEventListener('resize', scheduleFitCard);
      window.addEventListener('orientationchange', scheduleFitCard);
    });

    onBeforeUnmount(() => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      if (backgroundFadeTimer) {
        window.clearTimeout(backgroundFadeTimer);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      window.removeEventListener('resize', scheduleFitCard);
      window.removeEventListener('orientationchange', scheduleFitCard);
    });

    return {
      cardScale,
      backgroundStyle,
      contentRef,
      handleBackgroundIntroEnded,
      handleInput,
      handleLoopEnded,
      handleLoopTimeUpdate,
      introVideoRef,
      layoutMode,
      loginBgIntroSrc,
      loginBgPosterSrc,
      loginBgLoopSrc,
      loopPrimaryVideoRef,
      loopSecondaryVideoRef,
      naturalShellWidth,
      scaledBounds,
      showPassword,
      togglePasswordVisibility,
      visibleBackgroundLayer,
      visualOffsetY,
      viewportRef,
    };
  },
});
</script>

<style scoped>
.ai-login-page {
  position: absolute;
  inset: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  background: var(--login-bg-image) center / cover no-repeat;
  color: #16305e;
}

.login-background-video {
  position: fixed;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  opacity: 0;
  background: var(--login-bg-image) center / cover no-repeat;
  transition: opacity 0.18s ease-in-out;
}

.login-background-video--visible {
  opacity: 1;
}

.ai-login-page::-webkit-scrollbar {
  display: none;
}

.ambient-light {
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
  mix-blend-mode: screen;
}

.ambient-light__ray {
  position: absolute;
  height: 10vh;
  min-height: 72px;
  border-radius: 999px;
  opacity: 0.34;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.58) 42%,
    rgba(125, 205, 255, 0.36) 54%,
    transparent 100%
  );
  filter: blur(10px);
  transform-origin: center;
  animation: lightRayDrift 8s ease-in-out infinite;
}

.ambient-light__ray--one {
  top: 7%;
  left: -16%;
  width: 62vw;
  transform: rotate(17deg);
}

.ambient-light__ray--two {
  top: 16%;
  right: -18%;
  width: 58vw;
  transform: rotate(-19deg);
  animation-delay: -2.4s;
}

.ambient-light__ray--three {
  top: 47%;
  left: -22%;
  width: 74vw;
  transform: rotate(-8deg);
  opacity: 0.24;
  animation-delay: -4.6s;
}

.ambient-light__ray--four {
  top: 70%;
  right: -24%;
  width: 68vw;
  transform: rotate(10deg);
  opacity: 0.22;
  animation-delay: -6.1s;
}

.ambient-light__particle {
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow:
    0 0 8px rgba(255, 255, 255, 0.92),
    0 0 18px rgba(114, 216, 255, 0.76);
  animation: particleFloat 7.2s ease-in-out infinite;
}

.ambient-light__particle--one {
  left: 8%;
  top: 72%;
}

.ambient-light__particle--two {
  left: 18%;
  top: 63%;
  width: 4px;
  height: 4px;
  animation-delay: -1.1s;
}

.ambient-light__particle--three {
  left: 30%;
  top: 78%;
  animation-delay: -3.2s;
}

.ambient-light__particle--four {
  left: 42%;
  top: 58%;
  width: 3px;
  height: 3px;
  animation-delay: -2.1s;
}

.ambient-light__particle--five {
  left: 57%;
  top: 74%;
  animation-delay: -4.4s;
}

.ambient-light__particle--six {
  left: 70%;
  top: 64%;
  width: 4px;
  height: 4px;
  animation-delay: -5.7s;
}

.ambient-light__particle--seven {
  left: 84%;
  top: 49%;
  animation-delay: -2.8s;
}

.ambient-light__particle--eight {
  left: 91%;
  top: 76%;
  width: 4px;
  height: 4px;
  animation-delay: -6.5s;
}

.ambient-light__particle--nine {
  left: 66%;
  top: 33%;
  width: 3px;
  height: 3px;
  animation-delay: -3.9s;
}

.ambient-light__particle--ten {
  left: 24%;
  top: 34%;
  width: 3px;
  height: 3px;
  animation-delay: -5.2s;
}

@keyframes lightRayDrift {
  0%,
  100% {
    opacity: 0.18;
    translate: -2vw 0;
  }
  50% {
    opacity: 0.46;
    translate: 2vw 1.5vh;
  }
}

@keyframes particleFloat {
  0%,
  100% {
    opacity: 0.28;
    transform: translate3d(0, 0, 0) scale(0.78);
  }
  45% {
    opacity: 1;
    transform: translate3d(10px, -34px, 0) scale(1.14);
  }
  72% {
    opacity: 0.64;
    transform: translate3d(-8px, -58px, 0) scale(0.92);
  }
}

.circuit-flow {
  position: fixed;
  top: 13vh;
  right: 3vw;
  z-index: 1;
  width: min(42vw, 650px);
  height: min(50vh, 520px);
  pointer-events: none;
  overflow: visible;
  mix-blend-mode: screen;
}

.circuit-flow__trace {
  position: absolute;
  height: 3px;
  border-radius: 999px;
  opacity: 0.82;
  background:
    linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.72), transparent),
    linear-gradient(90deg, rgba(114, 216, 255, 0.1), rgba(114, 216, 255, 0.66));
  background-size:
    140px 100%,
    100% 100%;
  filter: drop-shadow(0 0 7px rgba(114, 216, 255, 0.85))
    drop-shadow(0 0 16px rgba(255, 255, 255, 0.55));
  animation: circuitTrace 3.8s linear infinite;
}

.circuit-flow__trace::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow:
    0 0 10px rgba(255, 255, 255, 0.98),
    0 0 22px rgba(114, 216, 255, 0.9),
    0 0 36px rgba(91, 140, 255, 0.45);
  transform: translate(-50%, -50%);
  animation: circuitElectron 3.8s linear infinite;
}

.circuit-flow__trace--one {
  top: 27%;
  right: 3%;
  width: 70%;
  transform: rotate(0deg);
}

.circuit-flow__trace--two {
  top: 35%;
  right: 10%;
  width: 57%;
  transform: rotate(0deg);
  animation-delay: -1.1s;
}

.circuit-flow__trace--two::before {
  animation-delay: -1.1s;
}

.circuit-flow__trace--three {
  top: 44%;
  right: 4%;
  width: 63%;
  transform: rotate(0deg);
  animation-delay: -2.2s;
}

.circuit-flow__trace--three::before {
  animation-delay: -2.2s;
}

.circuit-flow__trace--four {
  top: 20%;
  right: 25%;
  width: 34%;
  transform: rotate(-45deg);
  transform-origin: right center;
  animation-delay: -0.7s;
}

.circuit-flow__trace--four::before {
  animation-delay: -0.7s;
}

.circuit-flow__trace--five {
  top: 58%;
  right: 14%;
  width: 46%;
  transform: rotate(28deg);
  transform-origin: right center;
  animation-delay: -1.8s;
}

.circuit-flow__trace--five::before {
  animation-delay: -1.8s;
}

.circuit-flow__trace--six {
  top: 68%;
  right: 1%;
  width: 32%;
  transform: rotate(0deg);
  animation-delay: -2.9s;
}

.circuit-flow__trace--six::before {
  animation-delay: -2.9s;
}

.circuit-flow__node {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow:
    0 0 10px rgba(255, 255, 255, 0.96),
    0 0 26px rgba(114, 216, 255, 0.86),
    0 0 44px rgba(91, 140, 255, 0.38);
  animation: circuitNodePulse 2.6s ease-in-out infinite;
}

.circuit-flow__node--one {
  top: 26%;
  right: 72%;
}

.circuit-flow__node--two {
  top: 43%;
  right: 40%;
  animation-delay: -0.8s;
}

.circuit-flow__node--three {
  top: 66%;
  right: 20%;
  animation-delay: -1.6s;
}

@keyframes circuitTrace {
  from {
    background-position:
      -140px 0,
      0 0;
  }
  to {
    background-position:
      140px 0,
      0 0;
  }
}

@keyframes circuitElectron {
  0% {
    left: 0;
    opacity: 0;
  }
  12%,
  84% {
    opacity: 1;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
}

@keyframes circuitNodePulse {
  0%,
  100% {
    opacity: 0.42;
    transform: scale(0.72);
  }
  50% {
    opacity: 1;
    transform: scale(1.18);
  }
}

.login-stage {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: clamp(12px, 2vw, 24px) clamp(12px, 2vw, 24px)
    max(48px, env(safe-area-inset-bottom) + 36px);
  overflow: visible;
}

.ai-login-page--leaving .login-stage__content {
  animation: loginContentFadeOut 0.58s ease-in forwards;
  pointer-events: none;
}

.login-stage__viewport {
  width: 100%;
  min-height: calc(100vh - 2 * clamp(12px, 2vw, 24px));
  min-height: calc(100dvh - 2 * clamp(12px, 2vw, 24px));
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes loginContentFadeOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-18px) scale(0.985);
  }
}

.login-stage__shell {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-stage__shell--portrait {
  max-width: 560px;
}

.login-stage__shell--landscape {
  max-width: 1380px;
}

.login-stage__scale {
  transform-origin: center center;
  will-change: transform;
  width: 100%;
}

.login-stage__content {
  width: fit-content;
}

.login-stage__shell--portrait .login-stage__content,
.login-stage__shell--landscape .login-stage__content {
  width: 100%;
}

.hero-meta {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 10px;
  font-size: 24px;
}

.meta-copy {
  display: flex;
  flex-direction: column;
  gap: 5px;
  color: rgba(90, 127, 193, 0.9);
  font-size: 12px;
  letter-spacing: 0.12em;
}

.meta-copy span {
  line-height: 1.3;
}

.meta-copy small {
  color: rgba(110, 141, 196, 0.9);
  font-size: 11px;
  letter-spacing: 0.08em;
}

.meta-copy--right {
  align-items: flex-end;
  text-align: right;
}

.meta-dots {
  display: flex;
  align-items: center;
  gap: 6px;
}

.meta-dots i {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(135deg, #8fb2ff, #72d8ff);
  box-shadow: 0 0 12px rgba(114, 216, 255, 0.8);
}

.meta-dots::before {
  content: '';
  width: 34px;
  height: 2px;
  margin-right: 4px;
  background: linear-gradient(
    90deg,
    rgba(91, 140, 255, 0.85),
    rgba(114, 216, 255, 0.2)
  );
}

.hero-visual {
  margin-top: 6px;
}

.hero-title {
  margin-top: 18px;
  text-align: center;
}

.hero-title h1 {
  margin: 0;
  color: #0d2467;
  font-size: clamp(42px, 4.8vw, 76px);
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.06em;
}

.hero-title p {
  margin: 16px 0 0;
  color: #6581b7;
  font-size: clamp(18px, 1.8vw, 30px);
  letter-spacing: 0.1em;
}

.status-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  width: min(100%, 780px);
  margin: 26px auto 0;
  padding: 16px 20px;
  border-radius: 22px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.75),
    rgba(241, 248, 255, 0.55)
  );
  border: 1px solid rgba(164, 206, 255, 0.52);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.84),
    0 18px 36px rgba(128, 165, 227, 0.14);
}

.status-banner strong,
.status-banner span {
  display: block;
}

.status-banner--idle {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.64),
    rgba(244, 249, 255, 0.48)
  );
  border-color: rgba(177, 207, 246, 0.42);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.86),
    0 12px 24px rgba(132, 166, 222, 0.1);
}

.status-banner strong {
  color: #365697;
  font-size: 19px;
  font-weight: 700;
}

.status-banner span {
  margin-top: 6px;
  color: #6782b5;
  font-size: 14px;
  line-height: 1.5;
}

.status-badge {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  flex: 0 0 auto;
  background: radial-gradient(
    circle,
    #dff3ff 0%,
    #5b8cff 58%,
    rgba(91, 140, 255, 0.4) 100%
  );
  box-shadow:
    0 0 0 8px rgba(114, 216, 255, 0.14),
    0 0 18px rgba(91, 140, 255, 0.5);
  animation: pulseBadge 2.8s ease-in-out infinite;
}

.status-banner--idle .status-badge {
  background: radial-gradient(
    circle,
    #f5fbff 0%,
    #99b7f5 52%,
    rgba(153, 183, 245, 0.24) 100%
  );
  box-shadow:
    0 0 0 6px rgba(171, 208, 255, 0.1),
    0 0 12px rgba(130, 171, 237, 0.18);
  animation: none;
  opacity: 0.78;
}

.status-banner--error .status-badge {
  background: radial-gradient(
    circle,
    #fff0f3 0%,
    #ff8fa5 58%,
    rgba(255, 143, 165, 0.4) 100%
  );
  box-shadow:
    0 0 0 8px rgba(255, 143, 165, 0.12),
    0 0 18px rgba(255, 143, 165, 0.48);
}

.status-banner--verified .status-badge {
  background: radial-gradient(
    circle,
    #ebfff8 0%,
    #59d0a7 58%,
    rgba(89, 208, 167, 0.4) 100%
  );
  box-shadow:
    0 0 0 8px rgba(89, 208, 167, 0.14),
    0 0 18px rgba(89, 208, 167, 0.46);
}

.progress-grid {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto minmax(220px, 1fr);
  align-items: center;
  gap: 20px;
  margin-top: 34px;
}

.progress-core {
  display: flex;
  justify-content: center;
}

.auth-block {
  width: min(100%, 820px);
  margin: 34px auto 0;
}

.auth-label {
  display: inline-block;
  margin-bottom: 14px;
  color: #4e6ea9;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.auth-field-wrap {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  border-radius: 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.82),
    rgba(239, 246, 255, 0.68)
  );
  border: 1px solid rgba(143, 190, 255, 0.65);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.88),
    0 18px 38px rgba(138, 170, 225, 0.16);
  overflow: hidden;
}

.auth-field-wrap::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    transparent 18%,
    rgba(255, 255, 255, 0.5) 48%,
    transparent 72%
  );
  transform: translateX(-120%);
  animation: authShine 5.8s ease-in-out infinite;
  pointer-events: none;
}

.auth-icon {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(91, 140, 255, 0.18),
    rgba(114, 216, 255, 0.16)
  );
  color: #5b8cff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.auth-icon svg {
  width: 26px;
  height: 26px;
  fill: currentColor;
}

.auth-input {
  position: relative;
  z-index: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #234283;
  font-size: clamp(22px, 2vw, 34px);
  font-weight: 500;
  letter-spacing: 0.04em;
}

.auth-input::placeholder {
  color: rgba(85, 120, 184, 0.68);
}

.auth-state {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #5d7cb7;
  font-size: 18px;
  font-weight: 600;
}

.auth-state__dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 0 14px currentColor;
}

.auth-state--verified {
  color: #38b88a;
}

.auth-state--error {
  color: #ef6b8d;
}

.auth-state--verifying {
  color: #5b8cff;
}

.action-block {
  width: min(100%, 820px);
  margin: 34px auto 0;
}

.hero-footer {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  margin-top: 32px;
  color: #6d87b2;
  font-size: 18px;
  letter-spacing: 0.12em;
}

.hero-footer__wave {
  width: 56px;
  height: 16px;
  background:
    radial-gradient(circle at 6px 8px, #78adff 0 2px, transparent 2.6px),
    radial-gradient(circle at 16px 8px, #78adff 0 2px, transparent 2.6px),
    radial-gradient(circle at 26px 8px, #78adff 0 2px, transparent 2.6px),
    radial-gradient(circle at 36px 8px, #78adff 0 2px, transparent 2.6px),
    radial-gradient(circle at 46px 8px, #78adff 0 2px, transparent 2.6px);
  animation: wavePulse 2.5s ease-in-out infinite;
}

.hero-footer__line {
  width: 70px;
  height: 12px;
  background:
    radial-gradient(circle at 12px 6px, #a0c2ff 0 2px, transparent 2.6px),
    radial-gradient(circle at 28px 6px, #a0c2ff 0 2px, transparent 2.6px),
    linear-gradient(90deg, rgba(160, 194, 255, 0.82), rgba(160, 194, 255, 0.08));
  background-repeat: no-repeat;
  background-position:
    0 0,
    18px 0,
    36px 5px;
  background-size:
    20px 12px,
    20px 12px,
    34px 2px;
}

@keyframes pulseBadge {
  0%,
  100% {
    transform: scale(0.94);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.06);
    opacity: 1;
  }
}

@keyframes authShine {
  0%,
  72% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(120%);
  }
}

@keyframes wavePulse {
  0%,
  100% {
    opacity: 0.72;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

@media (max-width: 1080px) {
  .progress-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }

  .progress-core {
    order: -1;
  }
}

.ai-login-page--landscape :deep(.hud-card__body) {
  display: grid;
  grid-template-columns: minmax(320px, 0.8fr) minmax(540px, 1.2fr);
  grid-template-areas:
    'meta meta'
    'visual title'
    'visual status'
    'progress progress'
    'auth auth'
    'action action'
    'footer footer';
  column-gap: clamp(22px, 2.8vw, 44px);
  row-gap: 14px;
  align-items: center;
}

.ai-login-page--landscape .hero-meta {
  grid-area: meta;
  width: 100%;
  margin-bottom: 0;
}

.ai-login-page--landscape .hero-visual {
  grid-area: visual;
  margin-top: 0;
}

.ai-login-page--landscape .hero-title {
  grid-area: title;
  margin-top: 0;
  text-align: left;
}

.ai-login-page--landscape .hero-title h1 {
  font-size: clamp(56px, 4vw, 76px);
  line-height: 1.02;
}

.ai-login-page--landscape .hero-title p {
  margin-top: 8px;
  font-size: clamp(15px, 1.3vw, 22px);
}

.ai-login-page--landscape .status-banner {
  grid-area: status;
  width: 100%;
  margin: 0;
}

.ai-login-page--landscape .progress-grid {
  grid-area: progress;
  width: 100%;
  margin-top: 0;
}

.ai-login-page--landscape .auth-block {
  grid-area: auth;
  width: min(100%, 940px);
  justify-self: center;
  margin: 0;
}

.ai-login-page--landscape .action-block {
  grid-area: action;
  width: min(100%, 940px);
  justify-self: center;
  margin: 0;
}

.ai-login-page--landscape .hero-footer {
  grid-area: footer;
  justify-self: center;
  margin-top: 0;
}

@media (orientation: landscape) and (min-width: 960px) {
  .hero-title h1 {
    font-size: clamp(48px, 4vw, 84px);
  }

  .progress-grid {
    grid-template-columns: minmax(230px, 1fr) auto minmax(230px, 1fr);
    gap: clamp(18px, 2vw, 30px);
  }

  .auth-block,
  .action-block {
    width: min(100%, 940px);
  }
}

@media (orientation: landscape) and (min-width: 980px) and (max-height: 980px) {
  .ai-login-page--landscape :deep(.hud-card__body) {
    row-gap: 12px;
  }

  .ai-login-page--landscape .hero-title h1 {
    font-size: clamp(46px, 3.6vw, 66px);
  }

  .ai-login-page--landscape .hero-title p {
    margin-top: 6px;
    font-size: 15px;
  }

  .ai-login-page--landscape .status-banner {
    padding: 12px 16px;
  }

  .ai-login-page--landscape .status-banner strong {
    font-size: 15px;
  }

  .ai-login-page--landscape .status-banner span {
    font-size: 12px;
  }

  .ai-login-page--landscape .auth-block,
  .ai-login-page--landscape .action-block {
    margin-top: 0;
  }

  .ai-login-page--landscape .auth-label {
    margin-bottom: 8px;
    font-size: 14px;
  }

  .ai-login-page--landscape .auth-field-wrap {
    gap: 10px;
    padding: 12px 14px;
  }

  .ai-login-page--landscape .auth-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }

  .ai-login-page--landscape .auth-icon svg {
    width: 20px;
    height: 20px;
  }

  .ai-login-page--landscape .auth-input {
    font-size: 16px;
  }

  .ai-login-page--landscape .auth-state {
    gap: 8px;
    font-size: 12px;
  }

  .ai-login-page--landscape .hero-footer {
    font-size: 12px;
  }
}

@media (orientation: landscape) and (min-width: 980px) and (max-height: 860px) {
  .ai-login-page--landscape :deep(.hud-card__body) {
    grid-template-columns: minmax(280px, 0.74fr) minmax(480px, 1.26fr);
    row-gap: 10px;
  }

  .ai-login-page--landscape .hero-title h1 {
    font-size: clamp(40px, 3.2vw, 56px);
  }

  .ai-login-page--landscape .status-banner {
    padding: 10px 14px;
  }

  .ai-login-page--landscape .status-banner strong {
    font-size: 14px;
  }

  .ai-login-page--landscape .status-banner span {
    font-size: 11px;
  }
}

@media (max-width: 768px) {
  .login-stage {
    padding: 12px 12px max(56px, env(safe-area-inset-bottom) + 44px);
  }

  .login-stage__shell {
    width: 100%;
    max-width: 100%;
  }

  .hero-meta {
    margin-bottom: 0;
  }

  .meta-copy {
    font-size: 10px;
  }

  .meta-copy small {
    font-size: 9px;
  }

  .hero-title {
    margin-top: 10px;
  }

  .hero-title h1 {
    font-size: clamp(30px, 10vw, 46px);
  }

  .hero-title p {
    margin-top: 12px;
    font-size: 16px;
  }

  .status-banner {
    margin-top: 18px;
    padding: 14px 16px;
  }

  .status-banner strong {
    font-size: 15px;
  }

  .status-banner span {
    font-size: 12px;
  }

  .auth-label {
    font-size: 15px;
  }

  .auth-field-wrap {
    grid-template-columns: minmax(0, 1fr);
    justify-items: start;
    gap: 12px;
    padding: 16px;
  }

  .auth-input {
    width: 100%;
    font-size: 18px;
  }

  .auth-state {
    font-size: 14px;
  }

  .auth-block,
  .action-block,
  .status-banner {
    width: 100%;
  }

  .hero-footer {
    margin-top: 24px;
    gap: 10px;
    font-size: 13px;
  }

  .hero-footer__line {
    display: none;
  }
}

@media (max-width: 768px) and (orientation: portrait) {
  .login-stage {
    align-items: center;
    padding: 8px 10px max(28px, env(safe-area-inset-bottom) + 16px);
  }

  .login-stage__viewport {
    align-items: center;
  }

  .hero-meta {
    margin-bottom: 0;
  }

  .meta-copy {
    gap: 3px;
    font-size: 9px;
  }

  .meta-copy small {
    font-size: 8px;
  }

  .hero-visual {
    margin-top: -2px;
  }

  .hero-title {
    margin-top: 4px;
  }

  .hero-title h1 {
    font-size: clamp(24px, 7vw, 34px);
    line-height: 1.08;
  }

  .hero-title p {
    margin-top: 6px;
    font-size: 12px;
    letter-spacing: 0.06em;
  }

  .status-banner {
    gap: 10px;
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 16px;
  }

  .status-banner strong {
    font-size: 12px;
  }

  .status-banner span {
    margin-top: 3px;
    font-size: 10px;
    line-height: 1.35;
  }

  .status-badge {
    width: 14px;
    height: 14px;
  }

  .progress-grid {
    grid-template-columns: minmax(88px, 1fr) minmax(124px, 148px) minmax(
        88px,
        1fr
      );
    gap: 8px;
    margin-top: 10px;
  }

  .progress-core {
    order: 0;
  }

  .auth-block,
  .action-block {
    margin-top: 10px;
  }

  .auth-label {
    margin-bottom: 8px;
    font-size: 12px;
  }

  .auth-field-wrap {
    grid-template-columns: 34px minmax(0, 1fr) auto;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 16px;
  }

  .auth-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
  }

  .auth-icon svg {
    width: 18px;
    height: 18px;
  }

  .auth-input {
    font-size: 13px;
    letter-spacing: 0.02em;
  }

  .auth-state {
    gap: 6px;
    font-size: 11px;
  }

  .auth-state__dot {
    width: 8px;
    height: 8px;
  }

  .hero-footer {
    margin-top: 10px;
    gap: 8px;
    font-size: 10px;
    letter-spacing: 0.06em;
  }

  .hero-footer__wave {
    width: 34px;
    height: 10px;
    background:
      radial-gradient(circle at 4px 5px, #78adff 0 1.5px, transparent 1.8px),
      radial-gradient(circle at 12px 5px, #78adff 0 1.5px, transparent 1.8px),
      radial-gradient(circle at 20px 5px, #78adff 0 1.5px, transparent 1.8px),
      radial-gradient(circle at 28px 5px, #78adff 0 1.5px, transparent 1.8px);
  }
}

@media (orientation: landscape) and (max-height: 820px) {
  .login-stage {
    padding: 10px 14px;
  }

  .hero-meta {
    margin-bottom: 2px;
  }

  .hero-visual {
    margin-top: 0;
  }

  .hero-title {
    margin-top: 10px;
  }

  .hero-title h1 {
    font-size: clamp(28px, 4vw, 56px);
  }

  .hero-title p {
    margin-top: 8px;
    font-size: clamp(14px, 1.3vw, 20px);
  }

  .status-banner {
    width: min(100%, 880px);
    margin-top: 14px;
    padding: 12px 16px;
  }

  .status-banner strong {
    font-size: 14px;
  }

  .status-banner span {
    margin-top: 4px;
    font-size: 11px;
  }

  .progress-grid {
    grid-template-columns: minmax(160px, 1fr) minmax(180px, 250px) minmax(
        160px,
        1fr
      );
    gap: 12px;
    margin-top: 18px;
  }

  .auth-block,
  .action-block {
    width: min(100%, 860px);
    margin-top: 18px;
  }

  .auth-label {
    margin-bottom: 10px;
    font-size: 14px;
  }

  .auth-field-wrap {
    gap: 12px;
    padding: 12px 16px;
  }

  .auth-icon {
    width: 40px;
    height: 40px;
    border-radius: 14px;
  }

  .auth-icon svg {
    width: 20px;
    height: 20px;
  }

  .auth-input {
    font-size: clamp(14px, 1.8vw, 22px);
  }

  .auth-state {
    font-size: 12px;
    gap: 8px;
  }

  .hero-footer {
    margin-top: 18px;
    font-size: 12px;
  }
}

@media (orientation: landscape) and (max-height: 820px) and (max-width: 1080px) {
  .progress-grid {
    grid-template-columns: minmax(150px, 1fr) minmax(180px, 230px) minmax(
        150px,
        1fr
      );
  }

  .progress-core {
    order: 0;
  }
}

@media (max-width: 480px) {
  .login-stage {
    padding: 8px 8px max(64px, env(safe-area-inset-bottom) + 52px);
  }

  .meta-copy--right {
    display: none;
  }

  .hero-meta {
    justify-content: center;
  }

  .meta-copy {
    align-items: center;
    text-align: center;
  }

  .hero-title h1 {
    font-size: clamp(26px, 9vw, 38px);
  }

  .hero-title p {
    font-size: 14px;
  }

  .status-banner {
    gap: 12px;
    padding: 12px 14px;
    border-radius: 18px;
  }

  .status-banner strong {
    font-size: 14px;
  }

  .status-banner span {
    margin-top: 4px;
    font-size: 11px;
  }

  .auth-block,
  .action-block {
    margin-top: 18px;
  }

  .auth-field-wrap {
    padding: 14px;
    border-radius: 20px;
  }

  .auth-input {
    font-size: 16px;
  }

  .hero-footer {
    margin-top: 18px;
    font-size: 12px;
  }
}

@media (max-width: 480px) and (orientation: portrait) {
  .login-stage {
    padding: 6px 8px max(24px, env(safe-area-inset-bottom) + 14px);
  }

  .hero-title h1 {
    font-size: clamp(22px, 7.2vw, 30px);
  }

  .hero-title p {
    margin-top: 5px;
    font-size: 11px;
  }

  .status-banner {
    padding: 9px 10px;
  }

  .progress-grid {
    grid-template-columns: minmax(76px, 1fr) minmax(110px, 132px) minmax(
        76px,
        1fr
      );
    gap: 6px;
  }

  .auth-field-wrap {
    gap: 8px;
    padding: 9px 10px;
  }

  .auth-input {
    font-size: 12px;
  }

  .hero-footer {
    margin-top: 8px;
    font-size: 9px;
  }
}

.login-stage {
  padding: 0;
}

.login-stage__viewport {
  min-height: 100vh;
  min-height: 100dvh;
}

.login-stage__shell {
  width: auto;
  max-width: none;
}

.login-stage__scale {
  width: 600px;
  margin: 0 auto;
  transform-origin: center center;
}

.login-stage__content,
.login-stage__shell--portrait .login-stage__content,
.login-stage__shell--landscape .login-stage__content {
  width: 600px;
}

:deep(.hud-card) {
  width: 600px;
  max-width: none;
  padding: 34px 18px 28px;
}

:deep(.hud-card__body),
.ai-login-page--landscape :deep(.hud-card__body) {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-meta,
.ai-login-page--landscape .hero-meta {
  order: 1;
  width: 100%;
  justify-content: space-between;
  gap: 0;
  margin: 18px auto 0;
}

.meta-copy {
  gap: 2px;
  font-size: 28px;
  letter-spacing: 0.18em;
  white-space: nowrap;
}

.meta-copy small {
  font-size: 21px;
  letter-spacing: 0.12em;
}

.meta-copy--right {
  display: flex;
}

.meta-dots {
  gap: 4px;
}

.meta-dots i {
  width: 7px;
  height: 7px;
}

.meta-dots::before {
  display: none;
}

.hero-visual,
.ai-login-page--landscape .hero-visual {
  order: 2;
  width: 100%;
  margin-top: 22px;
}

:deep(.ai-hologram-brain) {
  height: 178px;
}

:deep(.brain-core) {
  width: 196px;
}

:deep(.brain-ai) {
  font-size: 74px;
  letter-spacing: 0;
}

:deep(.brain-orbit.orbit-one) {
  width: 220px;
  height: 66px;
}

:deep(.brain-orbit.orbit-two) {
  width: 236px;
  height: 82px;
}

:deep(.brain-platform) {
  bottom: 18px;
  width: 216px;
  height: 58px;
}

:deep(.brain-platform-ring) {
  bottom: 0;
  width: 270px;
  height: 88px;
}

:deep(.brain-beam) {
  bottom: 44px;
  width: 120px;
  height: 78px;
}

:deep(.brain-waves) {
  display: block;
  width: 54px;
  height: 32px;
}

:deep(.brain-waves--left) {
  left: 23%;
}

:deep(.brain-waves--right) {
  right: 23%;
}

.hero-title,
.ai-login-page--landscape .hero-title {
  order: 3;
  margin-top: 30px;
  text-align: center;
}

.hero-title h1,
.ai-login-page--landscape .hero-title h1 {
  color: #0b2b78;
  font-size: 54px;
  line-height: 1.16;
  font-weight: 800;
  letter-spacing: 0;
}

.hero-title p,
.ai-login-page--landscape .hero-title p {
  margin-top: 12px;
  color: rgba(28, 75, 149, 0.5);
  font-size: 24px;
  letter-spacing: 0.08em;
}

.status-banner,
.ai-login-page--landscape .status-banner {
  position: relative;
  order: 4;
  width: 100%;
  margin-top: 30px;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.75);
  box-shadow: none;
}

.status-banner > div,
.ai-login-page--landscape .status-banner > div {
  width: 100%;
  text-align: center;
}

.status-banner strong,
.ai-login-page--landscape .status-banner strong {
  color: #123a8a;
  font-size: 26px;
  line-height: 1.35;
}

.status-banner span,
.ai-login-page--landscape .status-banner span {
  margin-top: 3px;
  color: rgba(39, 91, 164, 0.6);
  font-size: 19px;
  line-height: 1.3;
}

.status-badge {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  box-shadow: 0 0 0 5px rgba(91, 140, 255, 0.1);
}

.progress-grid,
.ai-login-page--landscape .progress-grid {
  order: 5;
  width: 100%;
  display: grid;
  grid-template-columns: 174px 220px 174px;
  align-items: center;
  gap: 12px;
  margin-top: 40px;
}

.progress-core {
  order: 0;
}

:deep(.ai-module-list) {
  width: auto;
  gap: 18px;
  margin: 0;
}

:deep(.module-item) {
  gap: 7px;
}

:deep(.ai-module-list--right .module-item) {
  flex-direction: row-reverse;
  text-align: right;
}

:deep(.module-item::after) {
  display: block;
  width: 44px;
}

:deep(.ai-module-list--left .module-item::after) {
  right: -39px;
}

:deep(.ai-module-list--right .module-item::after) {
  left: -39px;
}

:deep(.module-item__icon) {
  width: 14px;
  height: 14px;
}

:deep(.module-item__icon::before) {
  width: 4px;
  height: 4px;
}

:deep(.module-item__copy) {
  gap: 2px;
}

:deep(.module-item__copy strong) {
  font-size: 25px;
  line-height: 1.2;
  white-space: nowrap;
}

:deep(.module-item__copy span) {
  font-size: 19px;
  line-height: 1.2;
  white-space: nowrap;
}

:deep(.ai-progress-ring) {
  width: 220px;
}

:deep(.ring-number span) {
  padding-left: 20px;
  font-size: 76px;
  letter-spacing: 0;
}

:deep(.ring-number small) {
  font-size: 22px;
}

:deep(.ring-copy p) {
  margin-top: 2px;
  font-size: 17px;
}

:deep(.ring-signal) {
  margin-top: 8px;
  height: 8px;
}

:deep(.ring-signal i) {
  width: 2px;
}

.auth-block,
.ai-login-page--landscape .auth-block {
  order: 6;
  width: 100%;
  margin-top: 32px;
}

.auth-label,
.ai-login-page--landscape .auth-label {
  margin-bottom: 7px;
  color: #17448c;
  font-size: 24px;
  letter-spacing: 0;
}

.auth-field-wrap,
.ai-login-page--landscape .auth-field-wrap {
  grid-template-columns: 34px minmax(0, 1fr) auto auto;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow: none;
}

.auth-field-wrap::after {
  display: none;
}

.auth-icon,
.ai-login-page--landscape .auth-icon {
  width: 30px;
  height: 30px;
  border-radius: 10px;
}

.auth-icon svg,
.ai-login-page--landscape .auth-icon svg {
  width: 15px;
  height: 15px;
}

.auth-input,
.ai-login-page--landscape .auth-input {
  font-size: 26px;
  letter-spacing: 0.06em;
}

.auth-visibility,
.ai-login-page--landscape .auth-visibility {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 10px;
  color: rgba(64, 99, 163, 0.78);
  background: rgba(255, 255, 255, 0.42);
  cursor: pointer;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
}

.auth-visibility:hover,
.ai-login-page--landscape .auth-visibility:hover {
  color: #2956a8;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 0 14px rgba(114, 216, 255, 0.28);
}

.auth-visibility svg,
.ai-login-page--landscape .auth-visibility svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

.auth-state,
.ai-login-page--landscape .auth-state {
  gap: 5px;
  font-size: 20px;
  white-space: nowrap;
}

.auth-state__dot {
  width: 5px;
  height: 5px;
}

.action-block,
.ai-login-page--landscape .action-block {
  order: 7;
  width: 100%;
  margin-top: 20px;
}

:deep(.ai-gradient-button) {
  min-height: 60px;
  grid-template-columns: auto 1fr;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 18px;
}

:deep(.ai-gradient-button__spinner) {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

:deep(.ai-gradient-button__copy) {
  align-items: flex-start;
  text-align: left;
}

:deep(.ai-gradient-button__copy strong) {
  font-size: 27px;
  line-height: 1.2;
}

:deep(.ai-gradient-button__copy small) {
  margin-top: 3px;
  font-size: 19px;
  line-height: 1.25;
}

.hero-footer,
.ai-login-page--landscape .hero-footer {
  order: 8;
  margin-top: 23px;
  gap: 10px;
  color: rgba(39, 91, 164, 0.52);
  font-size: 24px;
  letter-spacing: 0.1em;
}

.hero-footer__wave {
  width: 34px;
  height: 8px;
}

.hero-footer__line {
  display: none;
}

.login-stage {
  align-items: center;
}

.login-stage__viewport {
  align-items: center;
}

.login-stage__shell {
  margin-top: 0;
}
</style>
